import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface ExpenseResponse {
  record_id: string; expense_id: string; brain_id: string;
  service_name: string; category: string;
  amount_usd_cents: number; amount_ils_agorot: number; incurred_at: string;
}

interface CreateExpenseBody {
  service_name: string;
  category: string;
  amount_usd_cents: number;
  reference_id?: string;
  incurred_at?: string;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontSize: '8pt', fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: 'var(--omega-ink-subtle)',
  display: 'block', marginBottom: 4,
};

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceExpensesNewPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const navigate = useNavigate();

  const [serviceName, setServiceName] = useState('');
  const [category, setCategory] = useState('LLM');
  const [amountDollars, setAmountDollars] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [incurredAt, setIncurredAt] = useState('');

  const mutation = useMutation({
    mutationFn: (body: CreateExpenseBody) =>
      apiFetch<ExpenseResponse>('/finance/expenses', { method: 'POST', body }),
    onSuccess: () => navigate('/omega/finance/expenses/list'),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const amount_usd_cents = Math.round(parseFloat(amountDollars) * 100);
    const body: CreateExpenseBody = {
      service_name: serviceName,
      category,
      amount_usd_cents,
    };
    if (referenceId.trim()) body.reference_id = referenceId.trim();
    if (incurredAt) body.incurred_at = new Date(incurredAt).toISOString();
    mutation.mutate(body);
  }

  const isValid = serviceName.trim() !== '' && amountDollars !== '' && !isNaN(parseFloat(amountDollars)) && parseFloat(amountDollars) > 0;

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Expenses', to: '/omega/finance/expenses' },
        { label: 'New' },
      ]} />

      <div style={{ marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          New expense
        </h1>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 18 }}>

        <div>
          <label style={labelStyle}>Service name *</label>
          <input
            type="text"
            value={serviceName}
            onChange={e => setServiceName(e.target.value)}
            required
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            placeholder="OpenAI, AWS, Stripe…"
          />
        </div>

        <div>
          <label style={labelStyle}>Category *</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            required
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
          >
            <option value="LLM">LLM</option>
            <option value="infrastructure">infrastructure</option>
            <option value="api_cost">api_cost</option>
            <option value="other">other</option>
          </select>
        </div>

        <div>
          <label style={labelStyle}>Amount in USD (USD) *</label>
          <input
            type="number"
            value={amountDollars}
            onChange={e => setAmountDollars(e.target.value)}
            required
            min="0.01"
            step="0.01"
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            placeholder="0.00"
          />
        </div>

        <div>
          <label style={labelStyle}>Reference ID (optional)</label>
          <input
            type="text"
            value={referenceId}
            onChange={e => setReferenceId(e.target.value)}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            placeholder="INV-001, receipt-xyz…"
          />
        </div>

        <div>
          <label style={labelStyle}>Occurrence date (optional)</label>
          <input
            type="datetime-local"
            value={incurredAt}
            onChange={e => setIncurredAt(e.target.value)}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        {mutation.isError && (
          <div style={{ color: '#f87171', fontSize: '9pt' }}>
            {(mutation.error as Error).message}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="submit"
            disabled={!isValid || mutation.isPending}
            style={{
              background: isValid && !mutation.isPending ? 'rgba(110,231,183,0.15)' : 'rgba(255,255,255,0.04)',
              border: isValid && !mutation.isPending ? '1px solid rgba(110,231,183,0.4)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 4, color: isValid && !mutation.isPending ? '#6ee7b7' : 'var(--omega-ink-subtle)',
              fontFamily: 'var(--omega-mono)', fontSize: '9pt', fontWeight: 600,
              padding: '7px 18px', cursor: isValid && !mutation.isPending ? 'pointer' : 'not-allowed',
            }}
          >
            {mutation.isPending ? 'Saving…' : 'Save expense'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/omega/finance/expenses')}
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 4, color: 'var(--omega-ink-subtle)',
              fontFamily: 'var(--omega-mono)', fontSize: '9pt',
              padding: '7px 18px', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
